import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { style } from 'typestyle';
import DatePicker from 'react-date-picker';
import Switch from 'react-switch';
import { TextField } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment';
import type OfficeNotification from '../models/office-notification';
import { IsMobileContext } from '../../../core/context/is-mobile';
import * as styles from './modal.styles';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import StyleConstants from '../../../core/styles.constants';
import ConfirmationModal from './confirmation-modal.component';
import RecurringDays from '../recurring.component';
import TimeInput from '../../../core/components/time-input/time-input';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly initialNotification: OfficeNotification;
  readonly handleSave: (originalNotification: OfficeNotification, changedNotification: OfficeNotification) => Promise<boolean>;
  readonly notificationIsValid: (value: OfficeNotification) => boolean;
  readonly onDelete: (value: OfficeNotification) => Promise<boolean>;
}

const EditOfficeNotificationModal = ({
  showModal,
  handleClose,
  initialNotification,
  handleSave,
  onDelete,
  notificationIsValid,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [updateNotification, setUpdatedNotification] = useState<OfficeNotification>(initialNotification);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleCancelModal = (): void => {
    handleClose();
  };

  const handleNotificationChange = (field: keyof OfficeNotification, value: unknown): void => {
    const notificationCopy: OfficeNotification = { ...updateNotification };
    (notificationCopy[field] as unknown) = value;
    setUpdatedNotification(notificationCopy);
  };

  const handleUpdate = async (): Promise<void> => {
    if (isSaving) {
      return;
    }

    if (notificationIsValid(updateNotification)) {
      setIsSaving(true);
      const success = await handleSave(initialNotification, updateNotification);
      setIsSaving(false);
      if (success) {
        handleClose();
      }
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (isDeleting) {
      return;
    }

    setIsDeleting(true);
    const success = await onDelete(initialNotification);
    setIsDeleting(false);
    setShowDeleteModal(false);
    if (success) {
      handleClose();
    }
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCancelModal} style={styles.modalStyle(isMobile, 800, 'visible')}>
      <Column className={style(styles.modalBody(isMobile))} itemSpacing={28} mainAxisAlignment={MainAxisAlignment.Stretch}>
        <Row>
          <span className={style(styles.columnHeader(isMobile, 24))}>{t('edit-notification-title')}</span>
        </Row>
        <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
          <TextField
            label={t('office-notification-text-dutch').toString()}
            multiline
            maxRows={2}
            value={updateNotification.textDutch}
            onChange={(event): void => {
              handleNotificationChange('textDutch', event.target.value);
            }}
          />
        </Row>

        <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
          <TextField
            label={t('office-notification-text-english').toString()}
            multiline
            maxRows={2}
            value={updateNotification.textEnglish}
            onChange={(event): void => {
              handleNotificationChange('textEnglish', event.target.value);
            }}
          />
        </Row>

        <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
          <span className={style(styles.modalText)}>{t('choose-scheduled-date-time')}</span>
          <DatePicker
            clearIcon={null}
            allowPartialRange
            calendarClassName={style(styles.calendar)}
            format="dd/MM/yyyy"
            value={updateNotification.scheduledDateTime}
            minDate={new Date()}
            onChange={(value): void => {
              if (value instanceof Date) {
                handleNotificationChange('scheduledDateTime', value);
              }
            }}
          />
          <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
            <TimeInput
              hour={moment(updateNotification.scheduledDateTime).hours()}
              minute={moment(updateNotification.scheduledDateTime).minutes()}
              onChange={(hour, minute): void => {
                const updatedDateTime = moment(updateNotification.scheduledDateTime)
                  .set({ hour, minute })
                  .toDate();
                handleNotificationChange('scheduledDateTime', updatedDateTime);
              }}
            />
          </Row>
        </Row>

        <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
          <Switch
            checked={updateNotification.isRecurring}
            onChange={(value: boolean): void => {
              handleNotificationChange('isRecurring', value);
            }}
            onColor={StyleConstants.ORANGE_COLOR}
            onHandleColor={StyleConstants.WHITE_COLOR}
            handleDiameter={styles.switchDiameter}
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow={styles.switchBoxShadow}
            activeBoxShadow={styles.switchActiveBoxShadow}
            height={styles.switchHeight}
            width={styles.switchWidth}
          />
          <span className={style(styles.modalText)}>{t('is-recurring-office-notification')}</span>
        </Row>

        {updateNotification.isRecurring && (
          <>
            <Row crossAxisAlignment={CrossAxisAlignment.Center} itemSpacing={10}>
              <span className={style(styles.modalText)}>{t('choose-expiration-date')}</span>
              <DatePicker
                clearIcon={null}
                calendarClassName={style(styles.calendar)}
                format="dd/MM/yyyy"
                value={updateNotification.expirationDate}
                minDate={new Date()}
                onChange={(value): void => {
                  if (value instanceof Date) {
                    handleNotificationChange('expirationDate', value);
                  }
                }}
              />
            </Row>
            <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
              <RecurringDays
                selectedDays={updateNotification.recurringDays}
                onChange={(selectedDays: unknown): void => handleNotificationChange('recurringDays', selectedDays)}
              />
            </Row>
          </>
        )}

        <Row
          className={style(styles.deleteButton)}
          crossAxisAlignment={CrossAxisAlignment.Center}
          onClick={(): void => {
            setShowDeleteModal(true);
          }}
        >
          <span>{t('delete-office-notification')}</span>
          <DeleteOutlineIcon />
        </Row>
        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              loading={isSaving}
              fullWidth
              onClick={(): void => {
                handleUpdate();
              }}
            >
              {t('save')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCancelModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
      <ConfirmationModal
        showModal={showDeleteModal}
        itemName={t('notification')}
        isLoading={isDeleting}
        handleAccept={(): void => {
          handleDelete();
        }}
        handleClose={(): void => {
          setShowDeleteModal(false);
        }}
      />
    </Modal>
  );
};

export default EditOfficeNotificationModal;
